/* eslint-disable react-refresh/only-export-components */
import { useContext } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  useParams,
} from '@tanstack/react-router';
import { Loader } from '@progress/kendo-react-indicators';

import { RedirectToLoginAndBack } from '../admin/RedirectToLoginAndBack.tsx';
import { AdminLayout } from '../admin/AdminLayout.tsx';
import { SeminarsPage } from '../admin/seminars/SeminarsPage.tsx';
import { FunctionSettingsPage } from '../admin/FunctionSettingsPage.tsx';
import { AppearanceSettingsPage } from '../admin/AppearanceSettingsPage.tsx';
import { PublicSeminarPageFields } from '../admin/PublicSeminarPageFieldsPage.tsx';
import { CompanyFlagsPage } from '../admin/CompanyFlagsPage.tsx';
import { CompanyCategoriesPage } from '../admin/CompanyCategoriesPage.tsx';
import { MailSettingsPage } from '../admin/MailSettingsPage.tsx';
import { CompanySitesPage } from '../admin/company-sites/CompanySitesPage.tsx';
import { CompanySiteNewPage } from '../admin/company-sites/CompanySiteNewPage.tsx';
import { CompanySiteEditPage } from '../admin/company-sites/CompanySiteEditPage.tsx';
import { UsersPage } from '../admin/users/UsersPage.tsx';
import { UserNewPage } from '../admin/users/UserNewPage.tsx';
import { UserEditPage } from '../admin/users/UserEditPage.tsx';
import { SeminarEditPage } from '../admin/seminars/SeminarEditPage.tsx';
import { PublicLayout } from '../public/PublicLayout.tsx';
import { PublicSeminar } from '../public/PublicSeminar.tsx';
import { SignUpFormsPage } from '../admin/sign-up-form/SignUpFormsPage.tsx';
import { SignUpFormPage } from '../admin/sign-up-form/SignUpFormPage.tsx';
import { SignUpFormElementNewPage } from '../admin/sign-up-form/SignUpFormElementNewPage.tsx';
import { SignUpFormElementEditPage } from '../admin/sign-up-form/SignUpFormElementEditPage.tsx';
import { PublicRegistration } from '../public/PublicRegistration.tsx';
import { PublicSignUpPage } from '../public/PublicSignUpPage.tsx';
import { SignUpFormSelectPage } from '../admin/sign-up-form-select/SignUpFormSelectPage.tsx';
import { PublicSeminarList } from '../public/PublicSeminarList.tsx';
import { PublicSite } from '../public/PublicSite.tsx';
import {
  PublicCompanyContext,
  PublicCompanyProvider,
} from '../public/contexts/PublicCompanyProvider.tsx';
import { CompaniesProvider } from '../admin/contexts/CompaniesProvider.tsx';
import { MySeminarList } from '../public/MySeminarList.tsx';
import { MyLecturerSeminarList } from '../public/MyLecturerSeminarList.tsx';
import { SignUpConfirmationTemplateEditor } from '../admin/mail-templates/SignUpConfirmationTemplateEditor.tsx';
import { ReminderSevenDaysTemplateEditor } from '../admin/mail-templates/ReminderSevenDaysTemplateEditor.tsx';
import { ReminderOneDayTemplateEditor } from '../admin/mail-templates/ReminderOneDayTemplateEditor.tsx';
import { ReminderSevenDaysForLecturerTemplateEditor } from '../admin/mail-templates/ReminderSevenDaysForLecturerTemplateEditor.tsx';
import { ReminderOneDayForLecturerTemplateEditor } from '../admin/mail-templates/ReminderOneDayForLecturerTemplateEditor.tsx';
import { MyProfileData } from '../public/MyProfileData.tsx';
import {
  PublicUserContextProvider,
  PublicUserInfoContext,
} from '../public/contexts/PublicUserInfoContext.tsx';
import { MyLecturerProfile } from '../public/MyLecturerProfile.tsx';
import { SignUpConfirmationWaitlistTemplateEditor } from '../admin/mail-templates/SignUpConfirmationWaitlistTemplateEditor.tsx';
import { CalendarPage } from '../admin/CalendarPage.tsx';
import { MyAvailability } from '../public/MyAvailability.tsx';
import { SignUpCanceledTemplateEditor } from '../admin/mail-templates/SignUpCanceledTemplateEditor.tsx';
import { MyNotifications } from '../public/MyNotifications.tsx';
import { CompanyNewPage } from '../admin/company/CompanyNewPage.tsx';
import { ExportSignUpsPage } from '../admin/ExportSignUpsPage.tsx';
import { PublicSearch } from '../public/PublicSearch.tsx';
import { PublicProfileSidebar } from '../public/PublicProfileSidebar.tsx';
import { BackButton } from './BackButton.tsx';

// Routes only mount inside <PublicCompanyGate>, which guarantees publicCompany.id
// is defined before rendering — so we cast away the undefined.
const useCompanyId = (): string => {
  const [publicCompany] = useContext(PublicCompanyContext);
  return publicCompany?.id as string;
};

const usePublicCompanyOrThrow = () => {
  const [publicCompany] = useContext(PublicCompanyContext);
  if (!publicCompany?.id) {
    throw new Error(
      'usePublicCompanyOrThrow used outside <PublicCompanyGate>; publicCompany is undefined',
    );
  }
  return publicCompany;
};

const rootRoute = createRootRoute({
  component: () => (
    <PublicCompanyProvider>
      <PublicCompanyGate>
        <Outlet />
      </PublicCompanyGate>
    </PublicCompanyProvider>
  ),
});

const PublicCompanyGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [publicCompany] = useContext(PublicCompanyContext);
  return publicCompany?.id ? <>{children}</> : null;
};

// ─── /admin ────────────────────────────────────────────────────────────────
// /admin (no AdminLayout) — bare provider so the index redirect can render
const adminRootRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'admin',
  component: () => (
    <PublicUserContextProvider>
      <Outlet />
    </PublicUserContextProvider>
  ),
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRootRoute,
  path: '/',
  component: () => <RedirectToLoginAndBack target="/admin/seminars" />,
});

// Pathless layout that wraps every other admin page in CompaniesProvider + AdminLayout
const adminLayoutRoute = createRoute({
  getParentRoute: () => adminRootRoute,
  id: 'admin-layout',
  component: function AdminLayoutRouteComponent() {
    const companyId = useCompanyId();
    return (
      <CompaniesProvider>
        <AdminLayout companyId={companyId}>
          <Outlet />
        </AdminLayout>
      </CompaniesProvider>
    );
  },
});

const adminLeaf = <P extends string>(
  path: P,
  Component: React.FC<{ companyId: string }>,
) => {
  const RouteComponent: React.FC = () => {
    const companyId = useCompanyId();
    return <Component companyId={companyId} />;
  };
  return createRoute({
    getParentRoute: () => adminLayoutRoute,
    path,
    component: RouteComponent,
  });
};

const calendarRoute = adminLeaf('calendar', CalendarPage);
const exportSignUpsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'export-signups',
  component: () => <ExportSignUpsPage />,
});
const companyNewRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'company/new',
  component: () => <CompanyNewPage />,
});
const seminarsRoute = adminLeaf('seminars', SeminarsPage);
const functionRoute = adminLeaf('function', FunctionSettingsPage);
const appearanceRoute = adminLeaf('appearance', AppearanceSettingsPage);
const publicSeminarPageFieldsRoute = adminLeaf(
  'public-seminar-page-fields',
  PublicSeminarPageFields,
);
const signUpFormSelectRoute = adminLeaf(
  'sign-up-form-select',
  SignUpFormSelectPage,
);
const categoriesRoute = adminLeaf('categories', CompanyCategoriesPage);
const mailSettingsRoute = adminLeaf('mailsettings', MailSettingsPage);
const mailTplSignupConfirmationRoute = adminLeaf(
  'mailsettings/templates/signup-confirmation',
  SignUpConfirmationTemplateEditor,
);
const mailTplSignupConfirmationWaitlistRoute = adminLeaf(
  'mailsettings/templates/signup-confirmation-waitlist',
  SignUpConfirmationWaitlistTemplateEditor,
);
const mailTplReminderSevenDaysRoute = adminLeaf(
  'mailsettings/templates/reminder-seven-days',
  ReminderSevenDaysTemplateEditor,
);
const mailTplReminderOneDayRoute = adminLeaf(
  'mailsettings/templates/reminder-one-day',
  ReminderOneDayTemplateEditor,
);
const mailTplReminderSevenDaysLecturerRoute = adminLeaf(
  'mailsettings/templates/reminder-seven-days-lecturer',
  ReminderSevenDaysForLecturerTemplateEditor,
);
const mailTplReminderOneDayLecturerRoute = adminLeaf(
  'mailsettings/templates/reminder-one-day-lecturer',
  ReminderOneDayForLecturerTemplateEditor,
);
const mailTplSignupCanceledRoute = adminLeaf(
  'mailsettings/templates/signup-canceled',
  SignUpCanceledTemplateEditor,
);
const sitesRoute = adminLeaf('sites', CompanySitesPage);
const sitesNewRoute = adminLeaf('sites/new', CompanySiteNewPage);
const sitesEditRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'sites/edit/$slug',
  component: function SitesEditRouteComponent() {
    const companyId = useCompanyId();
    const { slug } = useParams({ from: sitesEditRoute.id });
    return <CompanySiteEditPage companyId={companyId} slug={slug} />;
  },
});
const flagsRoute = adminLeaf('flags', CompanyFlagsPage);
const signUpFormsRoute = adminLeaf('sign-up-forms', SignUpFormsPage);
const signUpFormRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'sign-up-forms/$formId',
  component: function SignUpFormRouteComponent() {
    const companyId = useCompanyId();
    const { formId } = useParams({ from: signUpFormRoute.id });
    return <SignUpFormPage companyId={companyId} formId={formId} />;
  },
});
const signUpFormFieldNewRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'sign-up-forms/$formId/fields/new',
  component: function SignUpFormFieldNewRouteComponent() {
    const companyId = useCompanyId();
    const { formId } = useParams({ from: signUpFormFieldNewRoute.id });
    return <SignUpFormElementNewPage companyId={companyId} formId={formId} />;
  },
});
const signUpFormFieldEditRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'sign-up-forms/$formId/fields/edit/$id',
  component: function SignUpFormFieldEditRouteComponent() {
    const companyId = useCompanyId();
    const { formId, id } = useParams({ from: signUpFormFieldEditRoute.id });
    return (
      <SignUpFormElementEditPage
        companyId={companyId}
        signUpFormElementId={id}
        formId={formId}
      />
    );
  },
});
const usersRoute = adminLeaf('users', UsersPage);
const usersNewRoute = adminLeaf('users/new', UserNewPage);
const usersEditRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'users/edit/$userId',
  component: function UsersEditRouteComponent() {
    const companyId = useCompanyId();
    const { userId } = useParams({ from: usersEditRoute.id });
    return <UserEditPage companyId={companyId} userId={userId} />;
  },
});
const seminarsEditRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'seminars/edit/$seminarId',
  component: function SeminarsEditRouteComponent() {
    const companyId = useCompanyId();
    const { seminarId } = useParams({ from: seminarsEditRoute.id });
    return <SeminarEditPage companyId={companyId} seminarId={seminarId} />;
  },
});
const seminarsEditBtsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'seminars/edit/bts/$btsSeminarId',
  component: function SeminarsEditBtsRouteComponent() {
    const companyId = useCompanyId();
    const { btsSeminarId } = useParams({ from: seminarsEditBtsRoute.id });
    return (
      <SeminarEditPage companyId={companyId} btsSeminarId={btsSeminarId} />
    );
  },
});

// ─── public ────────────────────────────────────────────────────────────────
// Pathless layout: PublicLayout wraps everything that isn't under /admin
const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public-layout',
  component: () => (
    <PublicUserContextProvider>
      <PublicLayout>
        <Outlet />
      </PublicLayout>
    </PublicUserContextProvider>
  ),
});

const PublicHomeComponent: React.FC = () => {
  const publicCompany = usePublicCompanyOrThrow();
  const companyId = useCompanyId();
  const { userInfo } = useContext(PublicUserInfoContext);
  if (!publicCompany?.functionalSettings.passwordActive) {
    return <PublicSeminarList companyId={companyId} />;
  }
  if (!userInfo?.data && !userInfo?.error) {
    return (
      <Loader
        type={'infinite-spinner'}
        className={
          'col-span-12 mt-8 justify-self-center !text-bts-theme-text-lighter'
        }
      />
    );
  }
  if (userInfo?.data?.currentUser) {
    return <PublicSeminarList companyId={companyId} />;
  }
  return <RedirectToLoginAndBack />;
};

const publicHomeRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/',
  component: PublicHomeComponent,
});

const PublicSearchComponent: React.FC<{ categoryId?: string }> = ({
  categoryId,
}) => {
  const publicCompany = usePublicCompanyOrThrow();
  const companyId = useCompanyId();
  if (!publicCompany?.functionalSettings.searchActive) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return <Navigate to={'/' as any} />;
  }
  return <PublicSearch companyId={companyId} categoryId={categoryId} />;
};

const publicSearchRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'search',
  component: () => <PublicSearchComponent />,
});

const publicSearchCategoryRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'search/$categoryId',
  component: function PublicSearchCategoryRouteComponent() {
    const { categoryId } = useParams({ from: publicSearchCategoryRoute.id });
    return <PublicSearchComponent categoryId={categoryId} />;
  },
});

const publicRegisterRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'register',
  component: function PublicRegisterRouteComponent() {
    const companyId = useCompanyId();
    return <PublicRegistration companyId={companyId} />;
  },
});

const publicCategoryRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'category/$categoryId',
  component: function PublicCategoryRouteComponent() {
    const companyId = useCompanyId();
    const { categoryId } = useParams({ from: publicCategoryRoute.id });
    return <PublicSeminarList companyId={companyId} categoryId={categoryId} />;
  },
});

const publicSeminarBtsRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'seminar/bts/$btsSeminarId',
  component: function PublicSeminarBtsRouteComponent() {
    const companyId = useCompanyId();
    const { btsSeminarId } = useParams({ from: publicSeminarBtsRoute.id });
    return <PublicSeminar companyId={companyId} btsSeminarId={btsSeminarId} />;
  },
});

const publicSeminarBtsSignUpRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'seminar/bts/$btsSeminarId/sign-up',
  component: function PublicSeminarBtsSignUpRouteComponent() {
    const companyId = useCompanyId();
    const { btsSeminarId } = useParams({
      from: publicSeminarBtsSignUpRoute.id,
    });
    return (
      <PublicSignUpPage companyId={companyId} btsSeminarId={btsSeminarId} />
    );
  },
});

const publicSeminarRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'seminar/$seminarId',
  component: function PublicSeminarRouteComponent() {
    const companyId = useCompanyId();
    const { seminarId } = useParams({ from: publicSeminarRoute.id });
    return <PublicSeminar companyId={companyId} seminarId={seminarId} />;
  },
});

const publicSeminarSignUpRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'seminar/$seminarId/sign-up',
  component: function PublicSeminarSignUpRouteComponent() {
    const companyId = useCompanyId();
    const { seminarId } = useParams({ from: publicSeminarSignUpRoute.id });
    return <PublicSignUpPage companyId={companyId} seminarId={seminarId} />;
  },
});

const publicSiteRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'site/$slug',
  component: function PublicSiteRouteComponent() {
    const companyId = useCompanyId();
    const { slug } = useParams({ from: publicSiteRoute.id });
    return <PublicSite companyId={companyId} slug={slug} />;
  },
});

// Pathless layout for /profile/* — keeps PublicProfileSidebar and the
// BackButton row mounted across navigations between profile pages so we
// don't get a flash/remount when switching tabs.
const profileLayoutRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  id: 'profile-layout',
  component: () => (
    <>
      <div className="col-start-1 col-end-13 flex flex-row">
        <BackButton href="/" title="Zurück (Katalog)" />
      </div>
      <PublicProfileSidebar />
      <div className="col-span-1 flex flex-col gap-6 md:col-span-9 md:col-start-4">
        <Outlet />
      </div>
    </>
  ),
});

const profileMySeminarsRoute = createRoute({
  getParentRoute: () => profileLayoutRoute,
  path: 'profile/myseminars',
  component: function ProfileMySeminarsRouteComponent() {
    const companyId = useCompanyId();
    return <MySeminarList companyId={companyId} />;
  },
});

const profileMyLecturerSeminarsRoute = createRoute({
  getParentRoute: () => profileLayoutRoute,
  path: 'profile/mylecturerseminars',
  component: function ProfileMyLecturerSeminarsRouteComponent() {
    const companyId = useCompanyId();
    return <MyLecturerSeminarList companyId={companyId} />;
  },
});

const profileDataRoute = createRoute({
  getParentRoute: () => profileLayoutRoute,
  path: 'profile/data',
  component: function ProfileDataRouteComponent() {
    const companyId = useCompanyId();
    return <MyProfileData companyId={companyId} />;
  },
});

const profileLecturerRoute = createRoute({
  getParentRoute: () => profileLayoutRoute,
  path: 'profile/lecturer',
  component: function ProfileLecturerRouteComponent() {
    const companyId = useCompanyId();
    return <MyLecturerProfile companyId={companyId} />;
  },
});

const profileAvailabilityRoute = createRoute({
  getParentRoute: () => profileLayoutRoute,
  path: 'profile/availability',
  component: function ProfileAvailabilityRouteComponent() {
    const companyId = useCompanyId();
    return <MyAvailability companyId={companyId} />;
  },
});

const profileNotificationsRoute = createRoute({
  getParentRoute: () => profileLayoutRoute,
  path: 'profile/notifications',
  component: function ProfileNotificationsRouteComponent() {
    const companyId = useCompanyId();
    return <MyNotifications companyId={companyId} />;
  },
});

const errorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'error',
  component: () => <h1>This page does not exist</h1>,
});

const routeTree = rootRoute.addChildren([
  adminRootRoute.addChildren([
    adminIndexRoute,
    adminLayoutRoute.addChildren([
      calendarRoute,
      exportSignUpsRoute,
      companyNewRoute,
      seminarsRoute,
      functionRoute,
      appearanceRoute,
      publicSeminarPageFieldsRoute,
      signUpFormSelectRoute,
      categoriesRoute,
      mailSettingsRoute,
      mailTplSignupConfirmationRoute,
      mailTplSignupConfirmationWaitlistRoute,
      mailTplReminderSevenDaysRoute,
      mailTplReminderOneDayRoute,
      mailTplReminderSevenDaysLecturerRoute,
      mailTplReminderOneDayLecturerRoute,
      mailTplSignupCanceledRoute,
      sitesRoute,
      sitesNewRoute,
      sitesEditRoute,
      flagsRoute,
      signUpFormsRoute,
      signUpFormRoute,
      signUpFormFieldNewRoute,
      signUpFormFieldEditRoute,
      usersRoute,
      usersNewRoute,
      usersEditRoute,
      seminarsEditRoute,
      seminarsEditBtsRoute,
    ]),
  ]),
  publicLayoutRoute.addChildren([
    publicHomeRoute,
    publicSearchRoute,
    publicSearchCategoryRoute,
    publicRegisterRoute,
    publicCategoryRoute,
    publicSeminarBtsRoute,
    publicSeminarBtsSignUpRoute,
    publicSeminarRoute,
    publicSeminarSignUpRoute,
    publicSiteRoute,
    profileLayoutRoute.addChildren([
      profileMySeminarsRoute,
      profileMyLecturerSeminarsRoute,
      profileDataRoute,
      profileLecturerRoute,
      profileAvailabilityRoute,
      profileNotificationsRoute,
    ]),
  ]),
  errorRoute,
]);

export const router = createRouter({
  routeTree,
  basepath: window.__BTS_EMBED_BASE_PREFIX__ ?? undefined,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
